import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { getCompanyData } from '~/data/company';
import { useSWR } from '~/hooks/useSWR';

import { ChatMessage } from '../types';
import { useChatBot } from '../useChatBot';

import { useSxStyles } from './useStyles';

const FAQ_QUESTIONS = [
  'How to edit new employee forms?',
  'Where can I find the employee headcount report?',
  'How to make scheduled employment changes?',
];

export const ChatBotWidget = ({
  hideTopHeader,
}: {
  hideTopHeader?: boolean;
}) => {
  const sx = useSxStyles();
  const loggedInId = useLoggedInEmployeeId();
  const { data: companyData } = useSWR('getCompanyData', getCompanyData);
  const companyName = companyData?.fComName;

  const {
    messages,
    isStreaming,
    send,
    hasActiveConversation,
    ensureActiveConversation,
  } = useChatBot({ userId: loggedInId, customerName: companyName });

  const [draft, setDraft] = useState('');
  const [startDraft, setStartDraft] = useState('');

  const doSend = async (text: string) => {
    const t = text.trim();
    if (!t) return;

    ensureActiveConversation();
    await send(t);
  };

  // Auto-scroll to bottom only when conversation is open
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastMessageContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    if (!hasActiveConversation) return;
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [hasActiveConversation, messages.length, isStreaming, lastMessageContent]);

  const canStartFromStartBox = startDraft.trim().length > 0;

  const startFromStartBox = async () => {
    if (!canStartFromStartBox) return;
    await doSend(startDraft);
    setStartDraft('');
  };

  return (
    <Box sx={sx.root}>
      {!hideTopHeader && (
        <Box sx={sx.header}>
          <Typography sx={sx.headerTitle}>solAIris</Typography>
        </Box>
      )}

      <Box sx={sx.body}>
        <Box sx={sx.scrollArea}>
          <Box sx={sx.scrollContent}>
            {!hasActiveConversation && (
              <Box sx={sx.landingColumn}>
                <Box sx={sx.intro}>
                  <Typography sx={sx.introTitle}>solAIris</Typography>
                  <Typography sx={sx.introSubtitle}>
                    Ready to help you
                  </Typography>
                </Box>
                <Box sx={sx.startCard}>
                  <IconButton
                    size="small"
                    sx={{
                      ...sx.plusBox,
                      ...(canStartFromStartBox
                        ? {}
                        : { opacity: 0.45, cursor: 'not-allowed' }),
                    }}
                    disabled={!canStartFromStartBox || isStreaming}
                    onClick={startFromStartBox}
                    title={
                      canStartFromStartBox
                        ? 'Start chat'
                        : 'Type a message to start'
                    }
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>

                  <TextField
                    value={startDraft}
                    onChange={(e) => setStartDraft(e.target.value)}
                    placeholder="Start by describing how I can help you?"
                    fullWidth
                    size="small"
                    sx={sx.startInput}
                    disabled={isStreaming}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        startFromStartBox();
                      }
                    }}
                  />
                </Box>

                {/* FAQ list */}
                {FAQ_QUESTIONS.map((q) => (
                  <Button
                    key={q}
                    variant="text"
                    sx={sx.suggestionCard}
                    disabled={isStreaming}
                    onClick={() => doSend(q)}
                  >
                    {q}
                  </Button>
                ))}
              </Box>
            )}

            {/* Active Conversation */}
            {hasActiveConversation && (
              <>
                <Box sx={{ ...sx.intro, mt: 1.5, mb: 1.5 }}>
                  <Typography sx={sx.introTitle}>solAIris</Typography>
                  <Typography sx={sx.introSubtitle}>
                    Ready to help you
                  </Typography>
                </Box>

                <Box sx={sx.messagesWrap}>
                  {messages.map((m: ChatMessage) => {
                    const isUser = m.role === 'user';
                    return (
                      <Box key={m.id} sx={sx.bubbleRow}>
                        <Box
                          sx={{
                            ...sx.bubble,
                            ...(isUser ? sx.bubbleUser : sx.bubbleAssistant),
                          }}
                        >
                          {isUser ? (
                            m.content
                          ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {m.content}
                            </ReactMarkdown>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={scrollRef} />
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Composer visible ONLY when an active conversation is open */}
        {hasActiveConversation && (
          <Box sx={sx.composer}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message…"
                fullWidth
                size="small"
                sx={sx.composerInput}
                disabled={isStreaming}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    doSend(draft);
                    setDraft('');
                  }
                }}
              />

              <IconButton
                onClick={() => {
                  doSend(draft);
                  setDraft('');
                }}
                disabled={isStreaming || !draft.trim()}
                title="Send"
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
